import { createTheme, PaletteColor, PaletteColorOptions, Theme as MuiTheme } from "@mui/material";
import { CSSProperties } from "@mui/material/styles/createMixins";
import type {} from "@mui/x-data-grid-premium/themeAugmentation";
import { once } from "lodash-es";

import { Colors } from "./colors";

declare module "@mui/material" {
    interface Theme {
        formMaxWidths: {
            card: number;
            cardContent: number;
            reviewCard: number;
            page: number;
        };
    }

    interface ThemeOptions {
        formMaxWidths: {
            card: number;
            cardContent: number;
            reviewCard: number;
            page: number;
        };
    }

    interface ActivityColor {
        avatar: PaletteColor;
    }

    interface ActivityColorOptions {
        avatar: PaletteColorOptions;
    }

    interface IBackgroundColors {
        appPrimary: PaletteColor; // main content -- light gray
        appSecondary: PaletteColor; // navbar -- white

        cardPrimary: PaletteColor; // form card -- white
        cardSecondary: PaletteColor; // form review card -- transparent white
        cardTertiary: PaletteColor; // recurring summary section, minimal info alert, linked entity card -- transparent gray
    }

    interface IBackgroundColorOptions {
        appPrimary: PaletteColorOptions;
        appSecondary: PaletteColorOptions;

        cardPrimary: PaletteColorOptions;
        cardSecondary: PaletteColorOptions;
        cardTertiary: PaletteColorOptions;
    }

    interface Palette {
        backgroundColors: IBackgroundColors;
        conversionColor: ActivityColor;
        depositColor: ActivityColor;
        paymentColor: ActivityColor;
    }

    interface PaletteOptions {
        backgroundColors: IBackgroundColorOptions;
        conversionColor: ActivityColorOptions;
        depositColor: ActivityColorOptions;
        paymentColor: ActivityColorOptions;
    }

    interface Mixins {
        textOverflowEllipsis: CSSProperties;

        navBackground: CSSProperties;
        creditCardBackground: CSSProperties;
        debitCardBackground: CSSProperties;
    }

    interface MixinsOptions {
        textOverflowEllipsis: CSSProperties;

        navBackground: CSSProperties;
        creditCardBackground: CSSProperties;
        debitCardBackground: CSSProperties;
    }

    interface SvgIconPropsSizeOverrides {
        title: true;
    }

    interface ZIndex {
        appHeader: number;
        appHeaderDrawer: number;
        appNavbar: number;
    }

    interface TypographyVariants {
        title1: React.CSSProperties;
        label1: React.CSSProperties;
        detail1: React.CSSProperties;
        nav1: React.CSSProperties;
        nav2: React.CSSProperties;
        link: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        title1?: React.CSSProperties;
        label1?: React.CSSProperties;
        detail1?: React.CSSProperties;
        nav1?: React.CSSProperties;
        nav2: React.CSSProperties;
        link?: React.CSSProperties;
    }

    interface TypographyPropsVariantOverrides {
        /**
         * Supported variants:
         */
        title1: true;
        h1: true;
        h2: true;
        body1: true;
        body2: true;
        label1: true;
        detail1: true;
        nav1: true;
        nav2: true;
        button: true;
        link: true;

        /**
         * Unsupported variants:
         */
        h3: false;
        h4: false;
        h5: false;
        h6: false;
        subtitle1: true;
        subtitle2: false;
        caption: false;
        overline: false;
    }
}

export const getMuiTheme = once(
    (): MuiTheme =>
        createTheme({
            palette: {
                divider: Colors.Gray[200],
                text: {
                    primary: Colors.Gray[800],
                    secondary: Colors.Gray[500],
                },
                primary: {
                    main: Colors.Blue[300],
                    light: Colors.Blue[50],
                    // dark: let MUI calculate this until our blue palette is updated
                    contrastText: Colors.Blue[50],
                },
                secondary: { main: Colors.Purple[500], light: Colors.Purple[50] },
                success: { main: Colors.Green[500], light: Colors.Green[50] },
                warning: { main: Colors.Orange[500], light: Colors.Orange[50] },
                error: { main: Colors.Red[400], light: Colors.Red[50] },

                backgroundColors: {
                    appPrimary: { main: Colors.Gray[50] },
                    appSecondary: { main: "#FFFFFF" },
                    cardPrimary: { main: "#FFFFFF" },
                    cardSecondary: { main: Colors.Gray[100] },
                    cardTertiary: {
                        main: Colors.Gray[100],
                        light: Colors.Gray[50],
                        dark: Colors.Gray[200],
                        contrastText: Colors.Gray[500],
                    },
                },
                conversionColor: { avatar: { main: Colors.Orange[400] } },
                depositColor: { avatar: { main: Colors.Green[300] } },
                paymentColor: { avatar: { main: Colors.Blue[300] } },
            },
            spacing: [4, 8, 12, 16, 20, 24, 32, 40, 48, 60, 80, 100, 120],
            // MUI requires 25 shadows ¯\_(ツ)_/¯
            shadows: [
                "none",
                "0 1px 2px 0 rgb(0 0 0 / 0.05)",
                "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
                "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
                "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                "0 25px 50px -12px rgb(0 0 0 / 0.25)",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
                "none",
            ],
            typography: {
                fontSize: 14,
                fontFamily: "Inter, sans-serif",
                button: {
                    fontWeight: 500,
                    lineHeight: 1,
                    textTransform: "capitalize",
                },
                title1: {
                    fontSize: 32,
                    fontWeight: 500,
                    lineHeight: 1.56,
                },
                h1: {
                    fontSize: 26,
                    fontWeight: 400,
                    lineHeight: 1.25,
                    color: Colors.Gray[700],
                },
                h2: {
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: 1.25,
                },
                h3: undefined,
                h4: undefined,
                h5: undefined,
                body1: {
                    fontSize: 14,
                    lineHeight: 1.286,
                },
                body2: {
                    fontSize: 14,
                    lineHeight: 1.286,
                    color: Colors.Gray[500],
                },
                label1: {
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: 1.35,
                    color: Colors.Gray[500],
                },
                detail1: {
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: 1.27,
                },
                nav1: {
                    fontSize: 14,
                    lineHeight: 1.2,
                },
                nav2: {
                    fontSize: 12,
                    lineHeight: 0.5,
                },
                link: {
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: 1.2,
                    borderBottom: "1px dashed",
                },
            },
            components: {
                MuiSvgIcon: {
                    variants: [
                        {
                            props: { fontSize: "title" },
                            style: {
                                fontSize: 60,
                            },
                        },
                    ],
                },
                // show expand/collapse button on the left instead of the default right
                // and fix icon to point down when expanded
                MuiAccordionSummary: {
                    styleOverrides: {
                        expandIconWrapper: {
                            marginRight: 10,
                            "&.Mui-expanded": {
                                transform: "rotate(90deg)",
                            },
                        },
                        root: {
                            flexDirection: "row-reverse",
                        },
                    },
                },
                MuiAlert: {
                    styleOverrides: {
                        root: ({ ownerState, theme }) => ({
                            borderRadius: 4,
                            ...(ownerState.severity === "info" && {
                                padding: theme.spacing(0, 4),
                                borderColor: Colors.Blue[100],
                                background: Colors.Blue[50],
                                color: Colors.Gray[600],
                                "& span": {
                                    fontWeight: 500,
                                },
                            }),
                        }),
                    },
                },
                MuiButton: {
                    defaultProps: {
                        disableElevation: true,
                    },
                    styleOverrides: {
                        root: ({ ownerState, theme }) => ({
                            ...(ownerState.size === "small" && {
                                padding: theme.spacing(0, 1),
                                "& svg:nth-of-type(1)": {
                                    fontSize: 12,
                                },
                            }),
                            ...(ownerState.size === "medium" && {
                                padding: theme.spacing(1, 2),
                                "& svg:nth-of-type(1)": {
                                    fontSize: 16,
                                },
                            }),
                            ...(ownerState.size === "large" && {
                                padding: theme.spacing(2, 3),
                                "& svg:nth-of-type(1)": {
                                    fontSize: 18,
                                },
                            }),
                            ...(ownerState.variant === "outlined" && {
                                backgroundColor: "#FFFFFF",
                                color: Colors.Gray[700],
                                borderColor: Colors.Gray[300],
                                "&:hover": {
                                    backgroundColor: Colors.Gray[100],
                                    borderColor: Colors.Gray[300],
                                },
                            }),
                            ...(ownerState.variant === "contained" && {
                                color: "#FFFFFF",
                            }),
                            ...(ownerState.variant === "text" && {
                                color: Colors.Gray[600],
                                "&:hover": {
                                    backgroundColor: Colors.Gray[100],
                                },
                            }),
                        }),
                    },
                },
                MuiButtonBase: {
                    defaultProps: {
                        disableRipple: true,
                    },
                },
                MuiButtonGroup: {
                    defaultProps: {
                        disableElevation: true,
                        disableRipple: true,
                    },
                },
                MuiFormGroup: {
                    styleOverrides: {
                        root: { columnGap: 8 },
                    },
                },
                MuiCheckbox: {
                    styleOverrides: {
                        root: {
                            position: "relative",
                            width: 18,
                            height: 18,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: Colors.Gray[300],
                            padding: 0,
                            "&.Mui-checked": {
                                border: "1px solid",
                                borderColor: Colors.Blue[300],
                                ".MuiSvgIcon-root": {
                                    display: "block",
                                },
                            },
                            "&:hover": {
                                borderColor: Colors.Blue[300],
                            },
                            "&.Mui-disabled": {
                                borderColor: Colors.Gray[200],
                            },
                            ".MuiSvgIcon-root": {
                                display: "none",
                            },
                        },
                    },
                },
                MuiRadio: {
                    styleOverrides: {
                        root: {
                            position: "relative",
                            width: 18,
                            height: 18,
                            borderRadius: 100,
                            border: "1px solid",
                            borderColor: Colors.Gray[300],
                            padding: 0,
                            "&.Mui-checked": {
                                borderColor: Colors.Blue[300],
                                ".MuiSvgIcon-root": {
                                    display: "block",
                                },
                            },
                            "&:hover": {
                                borderColor: Colors.Blue[300],
                            },
                            ".MuiSvgIcon-root": {
                                display: "none",
                            },
                        },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            columnGap: theme.spacing(1),
                            ".MuiListItemIcon-root": {
                                minWidth: 0,
                            },
                        }),
                    },
                },
                MuiSwitch: {
                    styleOverrides: {
                        root: {
                            width: 28,
                            height: 16,
                            padding: 0,
                            display: "flex",
                            "&:active": {
                                "& .MuiSwitch-thumb": {
                                    width: 15,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                    transform: "translateX(9px)",
                                },
                            },
                            "& .MuiSwitch-switchBase": {
                                padding: 2,
                                "&.Mui-checked": {
                                    transform: "translateX(12px)",
                                    color: "#fff",
                                    "& + .MuiSwitch-track": {
                                        opacity: 1,
                                        backgroundColor: Colors.Blue[300],
                                    },
                                },
                            },
                            "& .MuiSwitch-thumb": {
                                boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
                                width: 12,
                                height: 12,
                                borderRadius: 6,
                            },
                            "& .MuiSwitch-track": {
                                borderRadius: 16 / 2,
                                opacity: 1,
                                backgroundColor: Colors.Gray[300],
                                boxSizing: "border-box",
                            },
                        },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        root: ({ ownerState }) => ({
                            fontWeight: 500,
                            fontSize: 12,
                            borderRadius: 4,
                            color: Colors.Gray[700],
                            ...(ownerState.variant === "filled" &&
                                ownerState.color === "primary" && {
                                    backgroundColor: Colors.Blue[50],
                                    color: Colors.Blue[300],
                                    "&:.MuiSvgIcon-root": {
                                        color: Colors.Blue[300],
                                    },
                                }),
                            ...(ownerState.variant === "filled" &&
                                ownerState.color === "success" && {
                                    backgroundColor: Colors.Green[50],
                                    color: Colors.Green[500],
                                    "&:.MuiSvgIcon-root": {
                                        color: Colors.Green[500],
                                    },
                                }),
                            ...(ownerState.variant === "filled" &&
                                ownerState.color === "error" && {
                                    backgroundColor: Colors.Red[50],
                                    color: Colors.Red[500],
                                }),
                            ...(ownerState.variant === "filled" &&
                                ownerState.color === "info" && {
                                    backgroundColor: Colors.Blue[50],
                                    color: Colors.Blue[300],
                                    "&:.MuiSvgIcon-root": {
                                        color: Colors.Blue[300],
                                    },
                                }),
                            ...(ownerState.variant === "filled" &&
                                ownerState.color === "warning" && {
                                    backgroundColor: Colors.Orange[50],
                                    color: Colors.Orange[600],
                                }),
                            ...(ownerState.variant === "outlined" &&
                                ownerState.color === "default" && {
                                    borderColor: Colors.Gray[300],
                                }),
                        }),
                    },
                },
                MuiFormHelperText: {
                    styleOverrides: {
                        root: {
                            marginLeft: 0,
                        },
                    },
                },
                MuiFormLabel: {
                    styleOverrides: {
                        root: {
                            fontSize: 14,
                            color: Colors.Gray[500],
                            "&:not(.MuiInputLabel-root)": {
                                fontWeight: 400,
                            },
                        },
                    },
                },
                MuiFormControlLabel: {
                    styleOverrides: {
                        root: {
                            margin: 0,
                            columnGap: 8,
                        },
                    },
                },
                MuiLink: {
                    styleOverrides: {
                        root: {
                            color: Colors.Gray[500],
                            textDecoration: "none",
                            fontWeight: 500,
                            borderBottom: "1px dashed",
                            "&:hover": {
                                color: Colors.Gray[700],
                            },
                        },
                    },
                },
                MuiTabs: {
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                            minHeight: 0,
                            "& .MuiTabs-indicator": {
                                height: 1,
                            },
                        },
                    },
                },
                MuiToggleButtonGroup: {
                    styleOverrides: {
                        root: {
                            "& .MuiToggleButton-root": {
                                borderColor: Colors.Gray[300],
                                paddingLeft: 12,
                                paddingRight: 12,
                                color: Colors.Gray[500],
                                "&:hover": {
                                    background: "transparent",
                                    color: Colors.Gray[800],
                                },
                                "&.Mui-selected": {
                                    background: "white",
                                    color: Colors.Gray[800],
                                    "&:hover": {
                                        background: "white",
                                    },
                                },
                            },
                        },
                    },
                },
                MuiSelect: {
                    styleOverrides: {
                        select: () => ({
                            background: "white",
                        }),
                    },
                },
                MuiTab: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            textTransform: "none",
                            padding: theme.spacing(1, 1),
                            minHeight: 0,
                            borderRadius: 4,
                            fontWeight: 500,
                            minWidth: 0,
                            margin: theme.spacing(0),
                        }),
                    },
                },
                MuiToolbar: {
                    styleOverrides: {
                        root: {
                            height: 56,
                            minHeight: "56px !important",
                        },
                    },
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            fontSize: 14,
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            boxShadow: theme.shadows[1],
                            border: "1px solid",
                            borderColor: Colors.Gray[200],
                        }),
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: {
                            backgroundColor: "#FFFFFF",
                            "&.Mui-disabled": {
                                backgroundColor: Colors.Gray[50],
                            },
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            borderRadius: 4,
                            boxShadow: theme.shadows[1],
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: Colors.Gray[200],
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: Colors.Gray[300],
                            },
                            "&.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: Colors.Gray[400],
                                    borderWidth: 1,
                                },
                            },
                            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                                borderColor: Colors.Red[300],
                            },
                            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                                borderColor: Colors.Gray[200],
                            },
                        }),
                        input: {
                            "&::placeholder": {
                                color: Colors.Gray[400],
                                opacity: 1,
                            },
                        },
                    },
                },
                MuiTextField: {
                    defaultProps: {
                        variant: "outlined",
                        size: "small",
                    },
                    styleOverrides: {
                        root: {
                            "& .MuiFormHelperText-root": {
                                marginLeft: 0,
                            },
                        },
                    },
                },
            },
            mixins: {
                textOverflowEllipsis: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                },
                creditCardBackground: {},
                debitCardBackground: {},
                navBackground: {},
            },
            formMaxWidths: {
                card: 794,
                cardContent: 680,
                reviewCard: 440,
                page: 1298,
            },
            zIndex: {
                appHeader: 1250,
                appHeaderDrawer: 1275,
                appNavbar: 1260,
                drawer: 1300,
            },
        }),
);
